import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { buildMediaConstraints, readDevicePrefs } from '@/lib/zcallDevices';

// ─────────────────────────────────────────────────────────────
// ZCall — visioconférence WebRTC NATIVE (sans Jitsi).
//
// Topologie « maillage » (mesh) : chaque participant est connecté
// directement aux autres. La SIGNALISATION (échange des offres/réponses/ICE)
// passe par le temps réel du Backend (canal broadcast), le média circule en
// peer-to-peer.
//
// Point clé de fiabilité : deux participants ne doivent JAMAIS créer une
// offre en même temps (phénomène de « glare » qui casse la connexion et
// laisse chacun devant son propre écran). On applique donc une règle
// déterministe : c'est toujours le client dont l'identifiant est le plus
// grand qui envoie l'offre au plus petit. L'autre répond, puisqu'il reçoit
// une offre.
// ─────────────────────────────────────────────────────────────

const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env || {};

/**
 * Serveurs ICE — 100 % infrastructure Zifek.
 *
 * Aucun service tiers n'est utilisé : le STUN/TURN est le nôtre (coturn sur
 * notre VPS, exposé en `turn.zifek.fr`). Les valeurs viennent de l'env :
 *   VITE_PUBLIC_ZCALL_STUN_URL     (ex: stun:turn.zifek.fr:3478)
 *   VITE_PUBLIC_ZCALL_TURN_URL     (ex: turn:turn.zifek.fr:3478?transport=udp)
 *   VITE_PUBLIC_ZCALL_TURN_URLS    (liste séparée par des virgules)
 *   VITE_PUBLIC_ZCALL_TURN_USERNAME / VITE_PUBLIC_ZCALL_TURN_CREDENTIAL
 */
interface IceServerConfig {
  urls: string | string[];
  username?: string;
  credential?: string;
}

function buildIceServers(): IceServerConfig[] {
  const servers: IceServerConfig[] = [];
  const stun = env.VITE_PUBLIC_ZCALL_STUN_URL || 'stun:turn.zifek.fr:3478';
  servers.push({ urls: stun });

  const turnList = (env.VITE_PUBLIC_ZCALL_TURN_URLS || '')
    .split(',')
    .map((u) => u.trim())
    .filter(Boolean);
  if (env.VITE_PUBLIC_ZCALL_TURN_URL) turnList.unshift(env.VITE_PUBLIC_ZCALL_TURN_URL);

  if (turnList.length > 0) {
    servers.push({
      urls: turnList.length === 1 ? turnList[0] : turnList,
      username: env.VITE_PUBLIC_ZCALL_TURN_USERNAME || '',
      credential: env.VITE_PUBLIC_ZCALL_TURN_CREDENTIAL || '',
    });
  }
  return servers;
}

const ICE_SERVERS: IceServerConfig[] = buildIceServers();

export interface RemotePeer {
  id: string;
  name: string;
  stream: MediaStream | null;
}

interface SignalPayload {
  from: string;
  to?: string;
  name?: string;
  sdp?: unknown;
  candidate?: unknown;
  wantReply?: boolean;
}

interface ChannelLike {
  on: (
    type: 'broadcast',
    filter: { event: string },
    cb: (arg: { payload: SignalPayload }) => void,
  ) => ChannelLike;
  subscribe: (cb?: (status: string) => void) => ChannelLike;
  send: (args: { type: 'broadcast'; event: string; payload: SignalPayload }) => Promise<unknown>;
  unsubscribe: () => Promise<unknown>;
}

// Types paramètres des APIs WebRTC (évite de dépendre de types DOM globaux).
type RemoteDescArg = Parameters<RTCPeerConnection['setRemoteDescription']>[0];
type IceArg = Parameters<RTCPeerConnection['addIceCandidate']>[0];

interface PeerEntry {
  pc: RTCPeerConnection;
  name: string;
  /** Une négociation est en cours : on n'en lance pas une seconde. */
  negotiating: boolean;
  /** Nombre de tentatives de connexion (pour décider du redémarrage ICE). */
  attempts: number;
  retryTimer: number | null;
}

function randomId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID().slice(0, 12);
  } catch {
    /* fallback */
  }
  return `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export interface UseZCallResult {
  clientId: string;
  localStream: MediaStream | null;
  remotes: RemotePeer[];
  micOn: boolean;
  camOn: boolean;
  screenSharing: boolean;
  status: 'idle' | 'connecting' | 'connected' | 'error';
  error: string | null;
  toggleMic: () => void;
  toggleCam: () => void;
  setMicEnabled: (on: boolean) => void;
  setCamEnabled: (on: boolean) => void;
  startScreenShare: () => Promise<void>;
  stopScreenShare: () => Promise<void>;
}

export function useZCallWebRTC(
  roomId: string,
  userName: string,
  active: boolean,
  publish = true,
): UseZCallResult {
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remotes, setRemotes] = useState<RemotePeer[]>([]);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);
  const [status, setStatus] = useState<'idle' | 'connecting' | 'connected' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const clientIdRef = useRef<string>('');
  const userNameRef = useRef(userName);
  const localStreamRef = useRef<MediaStream | null>(null);
  const cameraTrackRef = useRef<MediaStreamTrack | null>(null);
  const peersRef = useRef<Map<string, PeerEntry>>(new Map());
  const remoteStreamsRef = useRef<Map<string, MediaStream>>(new Map());
  const pendingCandidatesRef = useRef<Map<string, unknown[]>>(new Map());
  const channelRef = useRef<ChannelLike | null>(null);
  const helloTimerRef = useRef<number | null>(null);

  const pushRemotes = useCallback(() => {
    const list: RemotePeer[] = [];
    peersRef.current.forEach((entry, id) => {
      list.push({ id, name: entry.name, stream: remoteStreamsRef.current.get(id) || null });
    });
    setRemotes(list);
  }, []);

  /** Un pair nous envoie-t-il réellement de la vidéo ? */
  const remoteHasVideo = useCallback((peerId: string): boolean => {
    const s = remoteStreamsRef.current.get(peerId);
    if (!s) return false;
    return s.getVideoTracks().some((t) => t.readyState === 'live' && !t.muted);
  }, []);

  const removePeer = useCallback((peerId: string) => {
    const entry = peersRef.current.get(peerId);
    if (entry) {
      if (entry.retryTimer) window.clearTimeout(entry.retryTimer);
      try { entry.pc.close(); } catch { /* déjà fermé */ }
      peersRef.current.delete(peerId);
    }
    remoteStreamsRef.current.delete(peerId);
    pendingCandidatesRef.current.delete(peerId);
    pushRemotes();
  }, [pushRemotes]);

  const getOrCreatePeer = useCallback((peerId: string, name: string): RTCPeerConnection => {
    const existing = peersRef.current.get(peerId);
    if (existing) {
      if (name && name !== existing.name) existing.name = name;
      return existing.pc;
    }

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    const ls = localStreamRef.current;
    if (ls) ls.getTracks().forEach((t) => { try { pc.addTrack(t, ls); } catch { /* ignore */ } });

    pc.onicecandidate = (e) => {
      if (e.candidate && channelRef.current) {
        void channelRef.current.send({
          type: 'broadcast',
          event: 'ice',
          payload: { from: clientIdRef.current, to: peerId, candidate: e.candidate.toJSON() },
        });
      }
    };
    pc.ontrack = (e) => {
      let stream = remoteStreamsRef.current.get(peerId);
      if (!stream) { stream = new MediaStream(); remoteStreamsRef.current.set(peerId, stream); }
      // Un même flux peut arriver par pistes séparées : on évite les doublons.
      if (!stream.getTracks().some((t) => t.id === e.track.id)) stream.addTrack(e.track);
      pushRemotes();
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed') {
        const entry = peersRef.current.get(peerId);
        if (entry) entry.negotiating = false;
        try { pc.restartIce(); } catch { /* ignore */ }
        // On relance une négociation complète (le pair a peut-être redémarré).
        window.setTimeout(() => {
          void channelRef.current?.send({
            type: 'broadcast',
            event: 'hello',
            payload: { from: clientIdRef.current, name: userNameRef.current, wantReply: true },
          });
        }, 800);
      }
      if (pc.connectionState === 'disconnected') {
        // Coupure passagère : on tente un redémarrage ICE avant tout.
        try { pc.restartIce(); } catch { /* ignore */ }
      }
      if (pc.connectionState === 'closed') removePeer(peerId);
    };

    peersRef.current.set(peerId, { pc, name: name || 'Participant', negotiating: false, retryTimer: null, attempts: 0 });
    pushRemotes();
    return pc;
  }, [pushRemotes, removePeer]);

  const flushPending = useCallback(async (peerId: string, pc: RTCPeerConnection) => {
    const pend = pendingCandidatesRef.current.get(peerId);
    if (!pend) return;
    for (const c of pend) { try { await pc.addIceCandidate(c as IceArg); } catch { /* ignore */ } }
    pendingCandidatesRef.current.delete(peerId);
  }, []);

  /** Envoie une offre — réservé au client « dominant » (anti-glare). */
  const createOffer = useCallback(async (peerId: string, name: string) => {
    if (!peerId || peerId === clientIdRef.current) return;
    if (!(clientIdRef.current > peerId)) return;

    const entry = peersRef.current.get(peerId);
    if (entry) {
      // Déjà connecté ET on reçoit bien sa vidéo : inutile de renégocier.
      if (entry.pc.connectionState === 'connected' && remoteHasVideo(peerId)) return;
      if (entry.negotiating) return;
      // Une négociation est déjà en cours côté signalisation : on laisse finir.
      if (entry.pc.signalingState !== 'stable') return;
    }

    const pc = getOrCreatePeer(peerId, name);
    const current = peersRef.current.get(peerId);
    if (current) {
      current.negotiating = true;
      current.attempts += 1;
    }
    try {
      // Deuxième essai (réseau capricieux) : on relance la collecte ICE.
      const restart = (current?.attempts || 1) > 1;
      const offer = await pc.createOffer(restart ? { iceRestart: true } : undefined);
      await pc.setLocalDescription(offer);
      await channelRef.current?.send({
        type: 'broadcast',
        event: 'offer',
        payload: { from: clientIdRef.current, to: peerId, name: userName, sdp: pc.localDescription },
      });
    } catch (e) {
      console.error('[zcall] createOffer', e);
    } finally {
      // On relâche le verrou une fois la réponse attendue (ou après délai).
      window.setTimeout(() => {
        const e = peersRef.current.get(peerId);
        if (e) e.negotiating = false;
      }, 6000);
    }
  }, [getOrCreatePeer, userName, remoteHasVideo]);

  const handleOffer = useCallback(async (p: SignalPayload) => {
    if (!p.from || !p.sdp) return;
    const pc = getOrCreatePeer(p.from, p.name || 'Participant');
    try {
      // Offre reçue alors qu'on avait déjà proposé : on annule notre offre
      // locale pour accepter la leur (évite l'état bloqué « have-local-offer »).
      if (pc.signalingState === 'have-local-offer') {
        try { await pc.setLocalDescription({ type: 'rollback' } as unknown as RemoteDescArg); } catch { /* ignore */ }
      }
      await pc.setRemoteDescription(p.sdp as RemoteDescArg);
      await flushPending(p.from, pc);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await channelRef.current?.send({
        type: 'broadcast',
        event: 'answer',
        payload: { from: clientIdRef.current, to: p.from, sdp: pc.localDescription },
      });
    } catch (e) {
      console.error('[zcall] handleOffer', e);
    }
  }, [getOrCreatePeer, flushPending]);

  const handleAnswer = useCallback(async (p: SignalPayload) => {
    if (!p.from || !p.sdp) return;
    const entry = peersRef.current.get(p.from);
    if (!entry) return;
    if (entry.pc.signalingState !== 'have-local-offer') return;
    try {
      await entry.pc.setRemoteDescription(p.sdp as RemoteDescArg);
      await flushPending(p.from, entry.pc);
    } catch (e) {
      console.error('[zcall] handleAnswer', e);
    }
  }, [flushPending]);

  const handleIce = useCallback(async (p: SignalPayload) => {
    if (!p.from || !p.candidate) return;
    const entry = peersRef.current.get(p.from);
    if (entry && entry.pc.remoteDescription) {
      try { await entry.pc.addIceCandidate(p.candidate as IceArg); } catch { /* ignore */ }
    } else {
      const arr = pendingCandidatesRef.current.get(p.from) || [];
      arr.push(p.candidate);
      pendingCandidatesRef.current.set(p.from, arr);
    }
  }, []);

  const replaceVideoTrack = useCallback(async (track: MediaStreamTrack | null) => {
    const senders: RTCRtpSender[] = [];
    peersRef.current.forEach((e) => {
      const s = e.pc.getSenders().find((x) => x.track && x.track.kind === 'video');
      if (s) senders.push(s);
    });
    for (const s of senders) { try { await s.replaceTrack(track); } catch { /* ignore */ } }
  }, []);

  const stopScreenShare = useCallback(async () => {
    const cam = cameraTrackRef.current;
    const audio = localStreamRef.current?.getAudioTracks() || [];
    const combined = new MediaStream([...(cam ? [cam] : []), ...audio]);
    localStreamRef.current = combined;
    setLocalStream(combined);
    await replaceVideoTrack(cam);
    setScreenSharing(false);
  }, [replaceVideoTrack]);

  const startScreenShare = useCallback(async () => {
    try {
      const display = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const track = display.getVideoTracks()[0];
      if (!track) return;
      const audio = localStreamRef.current?.getAudioTracks() || [];
      const combined = new MediaStream([track, ...audio]);
      localStreamRef.current = combined;
      setLocalStream(combined);
      await replaceVideoTrack(track);
      setScreenSharing(true);
      track.onended = () => { void stopScreenShare(); };
    } catch {
      /* partage d'écran annulé */
    }
  }, [replaceVideoTrack, stopScreenShare]);

  const toggleMic = useCallback(() => {
    setMicOn((v) => {
      const next = !v;
      localStreamRef.current?.getAudioTracks().forEach((t) => { t.enabled = next; });
      return next;
    });
  }, []);

  const toggleCam = useCallback(() => {
    setCamOn((v) => {
      const next = !v;
      if (!screenSharing) localStreamRef.current?.getVideoTracks().forEach((t) => { t.enabled = next; });
      return next;
    });
  }, [screenSharing]);

  /** Force l'état du micro (utilisé par « couper tout le monde » de l'hôte). */
  const setMicEnabled = useCallback((on: boolean) => {
    localStreamRef.current?.getAudioTracks().forEach((t) => { t.enabled = on; });
    setMicOn(on);
  }, []);

  /** Force l'état de la caméra. */
  const setCamEnabled = useCallback((on: boolean) => {
    if (!screenSharing) localStreamRef.current?.getVideoTracks().forEach((t) => { t.enabled = on; });
    setCamOn(on);
  }, [screenSharing]);

  // ── Cycle de vie : média local + signalisation + nettoyage ──
  useEffect(() => {
    if (!active || !roomId) return undefined;
    let cancelled = false;
    let pingTimer: number | null = null;
    let pingBurst: number[] = [];
    if (!clientIdRef.current) clientIdRef.current = randomId();
    userNameRef.current = userName;

    const setupMedia = async (): Promise<MediaStream | null> => {
      // Mode SPECTATEUR (live) : on ne publie rien, on ne fait que recevoir.
      if (!publish) {
        setCamOn(false);
        setMicOn(false);
        return null;
      }
      const prefs = readDevicePrefs();
      try {
        // On utilise la caméra et le micro choisis dans le test matériel.
        return await navigator.mediaDevices.getUserMedia(buildMediaConstraints(prefs));
      } catch {
        try {
          // Matériel mémorisé introuvable : on retombe sur les périphériques par défaut.
          return await navigator.mediaDevices.getUserMedia({
            video: { width: { ideal: 1280 }, height: { ideal: 720 } },
            audio: { echoCancellation: true, noiseSuppression: true },
          });
        } catch {
          try {
            const audioOnly = await navigator.mediaDevices.getUserMedia({ audio: true });
            setCamOn(false);
            return audioOnly;
          } catch {
            setMicOn(false);
            setCamOn(false);
            setError('Caméra et micro indisponibles — vous rejoignez en mode consultation.');
            return null;
          }
        }
      }
    };

    const start = async () => {
      setStatus('connecting');
      const stream = await setupMedia();
      if (cancelled) { stream?.getTracks().forEach((t) => t.stop()); return; }
      if (stream) {
        localStreamRef.current = stream;
        cameraTrackRef.current = stream.getVideoTracks()[0] || null;
        setLocalStream(stream);
      }

      const ch = supabase.channel(`zcall:${roomId}`, {
        config: { broadcast: { self: false } },
      }) as unknown as ChannelLike;
      channelRef.current = ch;

      ch.on('broadcast', { event: 'hello' }, ({ payload }) => {
        if (!payload.from || payload.from === clientIdRef.current) return;
        // Règle déterministe : seul le plus grand identifiant propose.
        // L'autre répond à l'invitation pour qu'une offre finisse par partir.
        if (clientIdRef.current > payload.from) {
          void createOffer(payload.from, payload.name || 'Participant');
        } else if (payload.wantReply) {
          void ch.send({
            type: 'broadcast',
            event: 'hello',
            payload: { from: clientIdRef.current, name: userName, wantReply: false },
          });
        }
      })
        .on('broadcast', { event: 'offer' }, ({ payload }) => {
          if (payload.to === clientIdRef.current) void handleOffer(payload);
        })
        .on('broadcast', { event: 'answer' }, ({ payload }) => {
          if (payload.to === clientIdRef.current) void handleAnswer(payload);
        })
        .on('broadcast', { event: 'ice' }, ({ payload }) => {
          if (payload.to === clientIdRef.current) void handleIce(payload);
        })
        .on('broadcast', { event: 'bye' }, ({ payload }) => {
          if (payload.from && payload.from !== clientIdRef.current) removePeer(payload.from);
        })
        .subscribe((st: string) => {
          if (st === 'SUBSCRIBED') {
            setStatus('connected');
            const announce = () => {
              if (cancelled) return;
              void ch.send({
                type: 'broadcast',
                event: 'hello',
                payload: { from: clientIdRef.current, name: userNameRef.current, wantReply: true },
              });
            };
            // Annonce répétée au démarrage : si un message de présence se perd,
            // une nouvelle offre finit toujours par être échangée.
            announce();
            pingBurst = [2000, 6000, 12000, 25000, 45000].map((ms) => window.setTimeout(announce, ms));
            pingTimer = window.setInterval(announce, 30000);

            // ── Chien de garde média ──────────────────────────────────────
            // Certains réseaux laissent la connexion « connected » alors que
            // l'image n'arrive pas (tracks muettes, première offre perdue).
            // Toutes les 5 s, si un pair ne nous envoie aucune vidéo, on
            // relance la négociation — côté dominant uniquement, pour ne
            // jamais provoquer de collision.
            helloTimerRef.current = window.setInterval(() => {
              if (cancelled) return;
              const ids = Array.from(peersRef.current.keys());
              // Un participant présent dans la salle mais absent du maillage
              // (offre perdue au démarrage) → on redemande sa présence.
              const known = new Set(ids);
              for (const id of ids) {
                if (id === clientIdRef.current) continue;
                if (remoteHasVideo(id)) continue;
                const entry = peersRef.current.get(id);
                if (!entry) continue;
                if (entry.negotiating || entry.pc.signalingState !== 'stable') continue;
                if (clientIdRef.current > id) {
                  void createOffer(id, entry.name);
                }
              }
              // Aucun pair connu : on redemande poliment qui est là.
              if (known.size === 0) {
                void ch.send({
                  type: 'broadcast',
                  event: 'hello',
                  payload: { from: clientIdRef.current, name: userNameRef.current, wantReply: true },
                });
              }
            }, 5000);
          }
        });
    };

    void start();

    return () => {
      cancelled = true;
      if (pingTimer != null) window.clearInterval(pingTimer);
      if (helloTimerRef.current != null) window.clearInterval(helloTimerRef.current);
      helloTimerRef.current = null;
      pingBurst.forEach((id) => window.clearTimeout(id));
      pingBurst = [];
      try { void channelRef.current?.send({ type: 'broadcast', event: 'bye', payload: { from: clientIdRef.current } }); } catch { /* ignore */ }
      peersRef.current.forEach((e) => {
        if (e.retryTimer) window.clearTimeout(e.retryTimer);
        try { e.pc.close(); } catch { /* ignore */ }
      });
      peersRef.current.clear();
      remoteStreamsRef.current.clear();
      pendingCandidatesRef.current.clear();
      try { void channelRef.current?.unsubscribe(); } catch { /* ignore */ }
      channelRef.current = null;
      localStreamRef.current?.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
      localStreamRef.current = null;
      cameraTrackRef.current = null;
      setLocalStream(null);
      setRemotes([]);
      setScreenSharing(false);
    };
  }, [active, roomId, userName, publish, createOffer, handleOffer, handleAnswer, handleIce, removePeer, remoteHasVideo]);

  return {
    clientId: clientIdRef.current,
    localStream,
    remotes,
    micOn,
    camOn,
    screenSharing,
    status,
    error,
    toggleMic,
    toggleCam,
    setMicEnabled,
    setCamEnabled,
    startScreenShare,
    stopScreenShare,
  };
}